import { Ajax2 } from '@shared/Ajax';


export interface IDocumentProperty {
    id?: number,
    uuid?: string,
    propertyKey: string,
    projectUuid?: string,
    organizationUuid?: string,
    labels: { [key: string]: string },
    schema: {
        type: 'string' | 'integer' | 'array' | string,
        format?: 'text' | string,
        pattern?: string,
        minimum?: string,
        exclusiveMinimum?: string,
        maximum?: string,
        exclusiveMaximum?: string,

        minItems?: number,
        maxItems?: number,
        items?: {
            type: 'string' | 'number' | string,
            enum: string[]
        };
        enum?: string[];

        // internal props for gui
        minimumMode?: 'inclusive' | 'exclusive'
        maximumMode?: 'inclusive' | 'exclusive'
        singleValue?: boolean; // can only have on value, not array of value
    };
}


export interface IDocumentPropertyAssign {
    defaultValue: string | string[];
    abstract: boolean;
    inheritable: boolean;
    deprecated: number | null;
    propertyDeclaration: IDocumentProperty;
}


function postProcessProperty(p: IDocumentProperty) {
    p.labels = p.labels ?? {};

    // per spec, handle non-array enum properties as min=max=1 arrays. dont remove this
    if (Array.isArray(p.schema.enum)) {
        p.schema = {
            type: 'array',
            minItems: 1,
            maxItems: 1,
            singleValue: true,
            items: {
                type: p.schema.type ?? 'string',
                enum: p.schema.enum
            }
        }
    }
}

export const DocumentPropertiesService = {
    typeLabels: {
        string: kuura.getMessage('general.data_type.string'),
        number: kuura.getMessage('general.data_type.number'),
        array: kuura.getMessage('js_properties.editor.type.select')
    },

    fetchAll: (orgUuid: string, projUuid: string): Promise<IDocumentProperty[]> => {
        return Ajax2({
            url: kuura.getContextPath() + `/api/1/property?organizationUuid=${orgUuid}&projectUuid=${projUuid}`,
            method: 'GET'
        }).then((resp: any) => {
            resp = resp.filter(f => !!f.schema);
            resp.forEach(postProcessProperty);
            return resp;
        })
    },

    fetchForCurrentProject: async () => {
        return DocumentPropertiesService.fetchAll(kuura.getProjectOrganization().uuid, kuura.getProjectUuid()).then(list => {
            return list.map(raw => {
                return {
                    defaultValue: null,
                    abstract: false,
                    inheritable: false,
                    deprecated: null,
                    propertyDeclaration: raw
                }
            }).sort((a, b) => a.propertyDeclaration.propertyKey.localeCompare(b.propertyDeclaration.propertyKey))
        })
    },

    create: (organizationUuid: string, projectUuid: string, property: IDocumentProperty) => {
        return Ajax2({
            url: kuura.getContextPath() + `/api/1/property`,
            method: 'POST',
            contentType: 'json',
            data: {
                ...property,
                projectUuid,
                organizationUuid
            }
        });
    },

    update: (organizationUuid: string, projectUuid: string, property: IDocumentProperty) => {
        return Ajax2({
            url: kuura.getContextPath() + `/api/2/property?organizationUuid=${organizationUuid}&projectUuid=${projectUuid}`,
            method: 'PUT',
            contentType: 'json',
            data: {
                propertyKey: property.propertyKey,
                propertyDeclaration: {
                    ...property,
                    projectUuid,
                    organizationUuid
                }
            }
        });
    },

    remove: (propertyKey: string, orgUuid: string, projUuid: string) => {
        return Ajax2({
            url: kuura.getContextPath() + `/api/2/property?organizationUuid=${orgUuid}&projectUuid=${projUuid}`,
            method: 'DELETE',
            contentType: 'json',
            data: {
                propertyKey: propertyKey
            }
        });
    },

    filter: (type: 'images' | 'documents', fillValues: Record<string, any>, logicSwitches: Record<string, 'and' | 'or'>, abortSignal?: AbortSignal) => {
        Object.keys(fillValues).forEach(k => {
            if (!Array.isArray(fillValues[k])) {
                fillValues[k] = [fillValues[k]];
            }
        })

        return Ajax2({
            url: `${kuura.getContextPath()}/api/1/document/filter/properties?type=${type}&projectUuid=${kuura.getProjectUuid()}`,
            method: 'POST',
            contentType: 'json',
            data: [{
                logic: "AND",
                conditions: [
                    {
                        logic: "AND",
                        criteria: Object.entries(fillValues)
                            .map(([key, val]) => ({
                                key,
                                values: val,
                                valueLogic: logicSwitches && logicSwitches[key] ? logicSwitches[key].toUpperCase() : 'OR'
                            }))
                    }
                ]
            }],
            signal: abortSignal
        }).then((resp: any) => {
            return resp;
        })
    },

    folder: {
        list: (folderUuid: string, abortSignal?: AbortSignal): Promise<IDocumentPropertyAssign[]> => {
            return Ajax2({
                url: kuura.getContextPath() + `/api/1/folder/${folderUuid}/properties`,
                signal: abortSignal
            }).then((resp: any) => {
                resp.forEach(p => {
                    postProcessProperty(p.propertyDeclaration);
                    if (p.defaultValue != null && p.defaultValue !== '') {
                        try {
                            p.defaultValue = JSON.parse(p.defaultValue)
                        } catch {
                            console.log('cant parse default property value', p.defaultValue);
                        }
                    }
                });
                return resp.filter(f => !!f.propertyDeclaration.schema);
            })
        },

        assign: (propertyKey: string, folderUuid: string, isAbstract: boolean, isInheritable: boolean, defaultValue: any, schemaType: string) => {
            if (defaultValue === '') {
                defaultValue = null;
            }
            if (defaultValue != null) {
                if (Array.isArray(defaultValue) && defaultValue.length === 0) {
                    defaultValue = null;
                }
                else if (schemaType === 'number') {
                    defaultValue = parseFloat(defaultValue);
                    if (isNaN(defaultValue)) {
                        defaultValue = null;
                    }
                }
            }

            const params = `isAbstract=${isAbstract ? 'true' : 'false'}&isInheritable=${isInheritable ? 'true' : 'false'}`;
            const opts: any = {
                url: `${kuura.getContextPath()}/api/1/folder/${folderUuid}/property/${propertyKey}?${params}`,
                method: 'PUT',
                contentType: 'json'
            };
            opts.data = defaultValue
            return Ajax2(opts);
        },

        unAssign: (propertyKey: string, folderUuid: string) => {
            return Ajax2({
                url: `${kuura.getContextPath()}/api/1/folder/${folderUuid}/property/${propertyKey}`,
                method: 'DELETE'
            });
        }
    },

    document: {
        saveValues: (docUuid: string, values: object, properties: IDocumentProperty[]) => {
            return Ajax2({
                url: `${kuura.getContextPath()}/api/1/document/${docUuid}/properties`,
                method: 'POST',
                contentType: 'json',
                data: sanitizeFillValuesBeforeSaving(values, properties)
            });
        },

        fetchDocument: (docUuid: string, abortSignal?: AbortSignal) => {
            return Ajax2({
                url: `${kuura.getContextPath()}/api/1/document/${docUuid}`,
                signal: abortSignal,
                method: 'GET'
            }).then(resp => {
                (resp.document.properties || []).forEach(p => {
                    try {
                        p.value = JSON.parse(p.value)
                    } catch {
                    }
                })
                return resp.document;
            });
        }
    },

    postProcessProperty,
    sanitizeFillValuesBeforeSaving
};

function sanitizeFillValuesBeforeSaving(values: object, properties: IDocumentProperty[]) {
    return Object.entries(values).map(([key, value]) => {
        const prop = properties.find(p => p.propertyKey === key);
        if (prop.schema.type === 'array') {
            // value can be an array but backend only supports strings and will parse that string into array when possible
            value = value != null ? value = JSON.stringify(value) : null;
        } else if (prop.schema.type === 'number') {
            value = value != null ? parseFloat(value) : null;
        } else if (prop.schema.type === 'string') {
            value = value ?? "";
        }
        return {
            key,
            value
        }
    })
}
